import {
  useMutation,
  useQuery,
  QueryClient,
  QueryKey,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { getPractitionerByEmail } from 'api/api';
import { QueryOptionsConfig } from 'api/hooks';
import {
  CreateGroupedOrganisationInput,
  CreateGroupRequest,
  CreateGroupResponse,
  Group,
  GroupResponse,
  PractitionerData,
  RequestError,
  UpdateGroupRequest,
  UpdateGroupResponse,
} from 'api/httpEntities';
import { QUERY_KEY_ROOTS } from 'variables';
import { PermissionRole } from 'pages/AddUser/AddUserForm/types';
import { User } from 'types/types';
import {
  createGroup,
  getGroup,
  getGroups,
  deleteGroup,
  updateGroup,
  createGroupedOrganisation,
  addOrganisationToGroup,
  addOrganisationsToGroup,
  removeOrganisationFromGroup,
  getGroupsByOrganisations,
  addExistingUserToOrgs,
  getExistingUserByGroup,
  getUsersByGroup,
  setRoleForUsersInGroup,
} from './organisationGroups';
import { AddExistingUserRequestData } from './organisationGroupsTypes';

export const useCreateGroup = (
  queryClient: QueryClient,
): UseMutationResult<CreateGroupResponse, Error, CreateGroupRequest, Error> =>
  useMutation({
    mutationFn: createGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUP_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUPS_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.GROUPS_DATA, QUERY_KEY_ROOTS.PAGINATED_DATA],
      });
    },
  });

export const useGetGroup = (
  groupId: string,
  config: QueryOptionsConfig<GroupResponse> = {},
): UseQueryResult<GroupResponse, Error> =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUP_DATA, groupId] as QueryKey,
    queryFn: () => getGroup(groupId),
    ...config,
  });

export const useGetAllGroups = (
  config?: any,
): UseQueryResult<GroupResponse[], Error> => {
  return useQuery<GroupResponse[], Error>({
    queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUPS_DATA],
    queryFn: async () => {
      const { organisationGroups } = await getGroups();
      return organisationGroups;
    },
    ...config,
  });
};

export const useDeleteGroup = (
  queryClient: QueryClient,
): UseMutationResult<void, Error, string, unknown> =>
  useMutation({
    mutationFn: deleteGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUP_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUPS_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.GROUPS_DATA, QUERY_KEY_ROOTS.PAGINATED_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATIONS_DATA],
      });
    },
  });

export const useUpdateGroup = (queryClient: QueryClient) =>
  useMutation<UpdateGroupResponse, Error, UpdateGroupRequest>({
    mutationFn: updateGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUP_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUPS_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.GROUPS_DATA, QUERY_KEY_ROOTS.PAGINATED_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATIONS_DATA],
      });
    },
  });

export const useCreateGroupedOrganisation = (
  queryClient: QueryClient,
): UseMutationResult<
  { id: string },
  Error,
  CreateGroupedOrganisationInput,
  Error
> =>
  useMutation({
    mutationFn: createGroupedOrganisation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUP_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUPS_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATIONS_DATA],
      });
    },
  });

export const useAddOrganisationToGroup = (
  queryClient: QueryClient,
  onError: (err: RequestError) => void = () => {},
) =>
  useMutation({
    mutationFn: addOrganisationToGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUP_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUPS_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.GROUPS_DATA, QUERY_KEY_ROOTS.PAGINATED_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATIONS_DATA],
      });
    },
    onError,
  });

export const useAddOrganisationsToGroup = (queryClient: QueryClient) =>
  useMutation({
    mutationFn: addOrganisationsToGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUP_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUPS_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATIONS_DATA],
      });
    },
  });

export const useRemoveOrganisationFromGroup = (
  queryClient: QueryClient,
  onError: (err: RequestError) => void = () => {},
) =>
  useMutation({
    mutationFn: removeOrganisationFromGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_GROUP_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.GROUPS_DATA, QUERY_KEY_ROOTS.PAGINATED_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATIONS_DATA],
      });
    },
    onError,
  });

export const useGetGroupByOrganisation = (
  id: string,
  config: QueryOptionsConfig<GroupResponse | null>,
) =>
  useQuery({
    queryKey: [
      QUERY_KEY_ROOTS.ORGANISATIONS_ORGANISATION_GROUP_DATA,
      id,
    ] as QueryKey,
    queryFn: async () => {
      const { organisationGroups } = await getGroupsByOrganisations({
        ids: [id],
      });
      return organisationGroups.length > 0 ? organisationGroups[0] : null;
    },
    ...config,
  });

export const useGetGroupsByOrganisations = (
  ids: string[],
  config: QueryOptionsConfig<GroupResponse[]>,
) =>
  useQuery({
    queryKey: [
      QUERY_KEY_ROOTS.ORGANISATIONS_ORGANISATION_GROUP_DATA,
      ids,
    ] as QueryKey,
    queryFn: async () => {
      const { organisationGroups } = await getGroupsByOrganisations({
        ids,
      });
      return organisationGroups;
    },
    ...config,
  });

export const useAddExistingUserToGroupOrgs = (): UseMutationResult<
  void,
  RequestError,
  AddExistingUserRequestData,
  unknown
> => {
  return useMutation({
    mutationFn: ({
      partnerGroupId,
      practitionerId,
      payload,
    }: {
      partnerGroupId: string;
      practitionerId: string;
      payload: { organisationIds: string[] };
    }) => {
      return addExistingUserToOrgs(partnerGroupId, practitionerId, payload);
    },
  });
};

export const useGetPractitionersByGroup = (
  groupId: string,
): UseQueryResult<
  {
    practitioners: PractitionerData[];
  } | null,
  RequestError
> => {
  return useQuery({
    queryKey: [groupId],
    queryFn: async () => getUsersByGroup(groupId),
    enabled: !!groupId,
  });
};

export const useSetRoleForUsersInGroup = (queryClient: QueryClient) =>
  useMutation({
    mutationFn: ({
      selectedGroup,
      selectedUsers,
      role,
    }: {
      selectedGroup: Group;
      selectedUsers: User[];
      role: PermissionRole;
    }) =>
      setRoleForUsersInGroup(
        selectedGroup.id,
        selectedUsers.map(({ id }) => id),
        role,
      ),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: [variables.selectedGroup.id],
      }),
  });

export const useGetPractitionerByGroupAndEmail = (
  groupId: string,
  email: string | undefined,
  isGM: boolean,
): UseQueryResult<PractitionerData | null, RequestError> => {
  const getExistingPractitioner =
    groupId && isGM
      ? () => getExistingUserByGroup(groupId, email)
      : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        () => getPractitionerByEmail(email!);
  return useQuery({
    queryKey: [email, groupId],
    queryFn: async () => getExistingPractitioner(),
    enabled: !!email,
  });
};
